/**
 * Spectate's "Mono" design system (web/DESIGN.md) on top of Zudoku's theme
 * variables: zero radius, 1px hairlines instead of shadows, Geist for reading
 * and Geist Mono for data, and yellow only as a signal. Yellow on white is
 * ~1.2:1, so in light mode lines and indicators are ink and yellow is a fill
 * only (`--primary-line`, `--primary-ink`, as in the dashboard).
 */
export const spectateCss = `
:root {
  --brand-yellow: #ffea00;
  --primary-line: #0a0a0a;
  --primary-ink: #756a00;
  --border-strong: #d4d4d4;
  --faint: #6b6b6b;
  --surface-2: #f2f2f2;
  --code-bg: #fafafa;
  --callout-note: #5c5c5c;
  --callout-tip: #137a39;
  --callout-info: #0369a0;
  --callout-caution: #9f5807;
  --callout-danger: #c92323;
}

.dark {
  --primary-line: #ffea00;
  --primary-ink: #ffea00;
  --border-strong: #2e2e2e;
  --faint: #808080;
  --surface-2: #161616;
  --code-bg: #0f0f0f;
  --callout-note: #a1a1a1;
  --callout-tip: #3ecf8e;
  --callout-info: #52a8ff;
  --callout-caution: #f5a524;
  --callout-danger: #f2555a;
}

body {
  font-feature-settings: "ss01", "cv11";
  -webkit-font-smoothing: antialiased;
}

::selection {
  background: color-mix(in oklab, var(--brand-yellow) 85%, transparent);
  color: #0a0a0a;
}

/* Lines, not shadows */
.shadow, .shadow-sm, .shadow-md, .shadow-lg, .shadow-xs {
  box-shadow: none !important;
}

/* Reading width and rhythm */
.typography {
  max-width: 44rem;
  font-size: 15px;
  line-height: 1.7;
}

.typography h1 {
  font-weight: 600;
  letter-spacing: -0.02em;
}

.typography h2,
.typography h3 {
  font-weight: 600;
  letter-spacing: -0.01em;
}

.typography h2 {
  border-top: 1px solid var(--border);
  padding-top: 1.75rem;
  margin-top: 2.5rem;
}

.typography a:not(.not-prose a) {
  text-decoration-line: underline;
  text-decoration-color: var(--border-strong);
  text-underline-offset: 4px;
  text-decoration-thickness: 1px;
  transition: text-decoration-color 120ms ease-out;
}

.typography a:not(.not-prose a):hover {
  text-decoration-color: var(--primary-line);
}

/* Inline code: mono, hairline frame, no fill noise */
.typography :not(pre) > code {
  font-size: 0.86em;
  padding: 0.1em 0.35em;
  border: 1px solid var(--border);
  background: var(--code-bg);
  border-radius: 0;
  font-weight: 450;
}

.typography :not(pre) > code::before,
.typography :not(pre) > code::after {
  content: none;
}

/* Tables: hairlines, sans headers in sentence case */
.typography table {
  font-size: 13.5px;
  border: 1px solid var(--border);
}

.typography thead th {
  background: var(--surface-2);
  font-weight: 500;
}

.typography th,
.typography td {
  padding: 0.55rem 0.8rem !important;
  border-color: var(--border) !important;
}

/* Key terms in bold stay foreground, never yellow */
.typography strong {
  font-weight: 600;
}

/* Active sidebar item: the 2px primary-line indicator of the dashboard. Not the top tabs in the
   header: they are NavLinks with aria-current too, and Zudoku underlines the active one */
nav [aria-current="page"]:not(header *) {
  box-shadow: inset 2px 0 0 0 var(--primary-line);
  font-weight: 500;
}

/* Focus: 1px primary-line, no halo */
:focus-visible {
  outline: 1px solid var(--primary-line) !important;
  outline-offset: 2px;
}

/* Header links (Status, Support, Dashboard): quiet text, no button plates */
header nav[aria-label="Main"] a {
  background: transparent !important;
  color: var(--muted-foreground);
  font-weight: 450;
  padding-inline: 0.6rem;
}

header nav[aria-label="Main"] a:hover {
  color: var(--foreground);
}

/* Cards: bento cells that share 1px borders */
.sp-card-grid {
  display: grid;
  gap: 1px;
  margin: 1.5rem 0;
  border: 1px solid var(--border);
  background: var(--border);
}

.sp-card {
  position: relative;
  padding: 1rem 1.1rem 1.1rem;
  background: var(--background);
  transition: background-color 120ms ease-out;
}

.sp-card:hover {
  background: var(--surface-2);
}

.sp-card:focus-within {
  outline: 1px solid var(--primary-line);
  outline-offset: -1px;
}

.sp-card-icon {
  display: block;
  width: 18px;
  height: 18px;
  margin-bottom: 0.75rem;
  color: var(--muted-foreground);
  transition: color 120ms ease-out;
}

.sp-card:hover .sp-card-icon {
  color: var(--primary-ink);
}

.sp-card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--foreground);
}

.sp-card-link {
  color: inherit;
  text-decoration: none;
}

.sp-card-link::after {
  content: "";
  position: absolute;
  inset: 0;
}

.sp-card-link:focus-visible {
  outline: none !important;
}

.sp-card-body {
  margin-top: 0.25rem;
  font-size: 13px;
  line-height: 1.45;
  color: var(--muted-foreground);
}

.sp-card-body p {
  margin: 0;
}

/* Page kicker: mono, uppercase, quiet */
.sp-eyebrow {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}

.sp-not-found {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 36rem;
  margin: 0 auto;
  padding: 6rem 1.5rem;
}

.sp-not-found h1 {
  margin: 0;
  font-size: 1.875rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.sp-not-found p:not(.sp-eyebrow) {
  margin: 0;
  color: var(--muted-foreground);
}

.sp-not-found ul {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sp-not-found a {
  text-decoration: underline;
  text-decoration-color: var(--border-strong);
  text-underline-offset: 4px;
}

/* Keyboard hints and HTTP method badges are data: mono */
kbd,
[data-method],
.font-mono {
  font-family: var(--font-mono);
}
`;
